import { getToken, getUserId, getUserName } from "./UserService";
import AppEnv from "../utils/AppEnv";
import { postData } from "./ApiService";

export interface WebSocketMessage {
  type: "category.save.started" | "category.save.completed";
  catalogId: string;
  categoryId: string;
  requestId?: string;
  updatedAt: string;
  statusCode?: number;
  errorMessage?: string;
}

type MessageHandler = (message: WebSocketMessage) => void;
type OnOpenHandler = () => void;
type OnReconnectHandler = () => void;

const SNAPSHOT_POLL_MS = 60 * 1000;
const RECONNECT_BASE_MS = 3000;
const RECONNECT_MAX_MS = 30000;

class AsyncEventsWebSocketService {
  private socket: WebSocket | null = null;
  private messageHandler: MessageHandler | null = null;
  private onOpenHandler: OnOpenHandler | null = null;
  private onReconnectHandler: OnReconnectHandler | null = null;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private snapshotPollingIntervalId: NodeJS.Timeout | null = null;
  private currentAccountId: string | null = null;
  private shouldReconnect: boolean = true;
  private isInitialConnection: boolean = true;
  private isReconnecting: boolean = false;
  private reconnectAttemptCount: number = 0;

  setMessageHandler(handler: MessageHandler): void {
    this.messageHandler = handler;
  }

  setOnOpenHandler(handler: OnOpenHandler): void {
    this.onOpenHandler = handler;
  }

  setOnReconnectHandler(handler: OnReconnectHandler): void {
    this.onReconnectHandler = handler;
  }

  /**
   * Start polling snapshot API when WebSocket is disconnected
   * This ensures we don't miss completed saves even if reconnection fails
   * @param callImmediately - If true, call snapshot immediately. If false, wait for polling interval.
   */
  startSnapshotPolling(callImmediately: boolean = true): void {
    // Don't restart polling if it's already running
    if (this.snapshotPollingIntervalId) {
      return;
    }
    
    if (!this.currentAccountId || !this.onReconnectHandler) {
      return;
    }

    console.log("🔄 Starting snapshot polling (every 60s) while WebSocket is disconnected");

    // Call snapshot immediately only if requested (not when reconnection is in progress)
    if (callImmediately) {
      this.onReconnectHandler();
    }

    this.snapshotPollingIntervalId = setInterval(() => {
      if (!this.isConnected() && this.shouldReconnect && this.currentAccountId) {
        console.log("🔄 Polling snapshot API (WebSocket still disconnected)");
        this.onReconnectHandler?.();
      } else {
        // WebSocket connected, stop polling
        console.log("✅ WebSocket connected, stopping snapshot polling");
        this.stopSnapshotPolling();
      }
    }, SNAPSHOT_POLL_MS);
  }

  /**
   * Schedules the next reconnect attempt. Must be used for every failed attempt: the socket
   * is not created when connect() bails early, so onclose never fires and the retry chain
   * would otherwise end permanently.
   */
  private scheduleReconnect(): void {
    if (!this.shouldReconnect || !this.currentAccountId) return;

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    this.reconnectAttemptCount++;
    const attemptCount = this.reconnectAttemptCount;
    const delay = Math.min(
      RECONNECT_BASE_MS * attemptCount,
      RECONNECT_MAX_MS,
    );

    this.reconnectTimeoutId = setTimeout(() => {
      if (this.shouldReconnect && this.currentAccountId) {
        console.log(`🔄 Reconnecting WebSocket... (attempt ${attemptCount})`);
        this.connect(this.currentAccountId, true);
      }
    }, delay);

    // Keep the fallback running until a connection is actually established.
    this.startSnapshotPolling(false);
  }

  /**
   * Stop polling snapshot API
   */
  stopSnapshotPolling(): void {
    if (this.snapshotPollingIntervalId) {
      clearInterval(this.snapshotPollingIntervalId);
      this.snapshotPollingIntervalId = null;
      console.log("🛑 Stopped snapshot polling");
    }
  }

  /**
   * Fetches a WebSocket connection ticket from the events-auth API
   */
  private async fetchWebSocketTicket(accountId: string): Promise<string | null> {
    try {
      const asyncRequestServiceUrl = process.env.REACT_APP_ASYNC_REQUEST_SERVICE_URL;
      if (!asyncRequestServiceUrl) {
        console.error("REACT_APP_ASYNC_REQUEST_SERVICE_URL is not configured");
        return null;
      }

      const endpoint = `${asyncRequestServiceUrl}/async/v1/${accountId}/events-auth`;
      const response = await postData(null, endpoint);

      // HttpClientImpl.post() returns response.data, so response is the actual response body
      if (response?.ticket) {
        return response.ticket;
      } else {
        console.error("Failed to fetch WebSocket ticket: invalid response", response);
        return null;
      }
    } catch (error) {
      // Single line, no error object: connect() reports the failure and this runs on every
      // retry, so dumping the axios error here floods the console during an outage.
      const reason = error instanceof Error ? error.message : String(error);
      console.warn(`Failed to fetch WebSocket ticket: ${reason}`);
      return null;
    }
  }

  async connect(accountId: string, isReconnect: boolean = false): Promise<void> {
    if (this.socket?.readyState === WebSocket.OPEN) return;

    // Clear any pending reconnection attempts
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Track if this is initial connection or reconnection
    if (!isReconnect && accountId !== this.currentAccountId) {
      // New account or first connection
      this.isInitialConnection = true;
      this.isReconnecting = false;
    } else if (isReconnect) {
      // This is a reconnection
      this.isInitialConnection = false;
      this.isReconnecting = true;
    }

    this.currentAccountId = accountId;
    this.shouldReconnect = true;

    const websocketUrl = AppEnv.WEBSOCKET_ASYNC_EVENTS_URL;
    if (!websocketUrl || websocketUrl === "undefined" || websocketUrl.includes("undefined")) {
      console.error("WEBSOCKET_ASYNC_EVENTS_URL is not configured or invalid:", websocketUrl);
      this.scheduleReconnect();
      return;
    }

    const userId = getUserId();
    const userName = getUserName() ?? "";
    const token = await getToken();

    if (!userId || !token || !accountId) {
      console.error("Missing userId, token or accountId, cannot connect");
      this.scheduleReconnect();
      return;
    }

    // Fetch WebSocket ticket from events-auth API
    const ticket = await this.fetchWebSocketTicket(accountId);
    if (!ticket) {
      console.error("Failed to fetch WebSocket ticket, cannot connect");
      this.scheduleReconnect();
      return;
    }

    const queryParams = new URLSearchParams({
      "x-account-id": accountId,
      "x-user-id": userId,
      "x-user-name": userName,
      token: ticket,
    });

    // Ensure baseUrl has a trailing slash, then add query params
    const baseUrl = websocketUrl.endsWith("/") ? websocketUrl : `${websocketUrl}/`;
    const wsUrl = `${baseUrl}?${queryParams.toString()}`;

    this.socket = new WebSocket(wsUrl);
    this.setupEventHandlers();
  }

  private setupEventHandlers(): void {
    if (!this.socket) return;

    this.socket.onopen = () => {
      const wasReconnecting = this.isReconnecting;
      const attemptCount = this.reconnectAttemptCount;
      
      console.log("WebSocket connected", { 
        wasReconnecting, 
        reconnectAttemptCount: attemptCount,
        timestamp: new Date().toISOString()
      });

      // Stop snapshot polling since we're connected now
      this.stopSnapshotPolling();

      // Reset reconnect attempt count on successful connection
      this.reconnectAttemptCount = 0;
      
      if (this.isInitialConnection) {
        // Initial connection - fetch snapshot
        this.onOpenHandler?.();
        this.isInitialConnection = false;
      } else if (wasReconnecting) {
        // Reconnection - fetch snapshot to catch missed messages and check for completed saves
        console.log(`🔄 WebSocket reconnected - fetching snapshot to catch missed messages - ${new Date().toISOString()}`);
        this.onReconnectHandler?.();
        this.isReconnecting = false;
      }
    };

    this.socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log("📨 WebSocket message received");
        
        if (this.messageHandler) {
          this.messageHandler(message);
        } else {
          console.warn("⚠️ No message handler set! Message will be lost:", message);
        }
      } catch (error) {
        console.error("❌ Invalid WebSocket message:", error, event.data);
      }
    };

    this.socket.onerror = () => {
      // Transport-level only
      console.error("WebSocket transport error");
    };

    this.socket.onclose = (event) => {
      console.warn("WebSocket closed", { 
        code: event.code, 
        reason: event.reason,
        reconnectAttemptCount: this.reconnectAttemptCount 
      });
      this.socket = null;

      // Attempt to reconnect if connection was not intentionally closed
      // Don't reconnect for normal closure (1000) or if shouldReconnect is false
      if (this.shouldReconnect && event.code !== 1000 && this.currentAccountId) {
        this.scheduleReconnect();
      } else {
        // Normal closure or intentional disconnect - stop polling
        this.stopSnapshotPolling();
      }
    };
  }

  disconnect(): void {
    this.shouldReconnect = false;
    
    // Clear any pending reconnection
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }

    // Stop snapshot polling
    this.stopSnapshotPolling();

    this.socket?.close();
    this.socket = null;
    this.currentAccountId = null;
    this.isInitialConnection = true;
    this.reconnectAttemptCount = 0;
  }

  isConnected(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }
}

export const asyncEventsWebSocketService = new AsyncEventsWebSocketService();
